import { FaUniversity } from "react-icons/fa";
import { useTheme } from "../context/ThemeContext";
import { useCollege } from "../context/CollegeContext";
import { useAuth } from "../context/AuthContext";
import CollegeSelector from "../components/CollegeSelector";
import FileUpload from "../components/FileUpload";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { toast } from "react-toastify";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiUser } from "react-icons/fi";
import { FaExclamationTriangle } from "react-icons/fa";

const HomePage = () => {
  const { darkMode } = useTheme();
  const { selectedCollege } = useCollege();
  const { currentUser, phoneNumber, updatePhoneNumber, name, updateName } = useAuth();
  const [isUploadEnabled, setIsUploadEnabled] = useState(true);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [newName, setNewName] = useState("");

  // Check if phoneNumber or name exists
  useEffect(() => {
    if (currentUser && (!phoneNumber || !name)) {
      setShowPhoneModal(true);
    } else {
      setShowPhoneModal(false);
    }
  }, [currentUser, phoneNumber, name]);

  // Real-time listener for upload status
  useEffect(() => {
    const docRef = doc(db, "settings", "uploadStatus");

    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const uploadStatus = docSnap.data().enabled;
          setIsUploadEnabled(uploadStatus);
        } else {
          console.log("Upload status document not found.");
          toast.error("Upload status not found!");
        }
      },
      (error) => {
        console.error("Error listening to upload status:", error);
        toast.error("Failed to fetch upload status.");
      }
    );

    return () => unsubscribe();
  }, []);

  // Disable scrolling when phone modal is shown
  useEffect(() => {
    if (showPhoneModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showPhoneModal]);

  // Handle phone number and name submission
  const handlePhoneNumberSubmit = useCallback(async () => {
    try {
      if (!phoneNumber && !newPhoneNumber) {
        toast.error("Please enter a valid phone number.");
        return;
      }
      if (!name && !newName) {
        toast.error("Please enter a valid name.");
        return;
      }

      if (!phoneNumber) {
        await updatePhoneNumber(newPhoneNumber);
      }
      if (!name) {
        await updateName(newName);
      }

      setShowPhoneModal(false);
      toast.success("Information updated successfully!");
    } catch (error) {
      console.error("Error updating phone number or name:", error);
      toast.error("Failed to update information.");
    }
  }, [newPhoneNumber, newName, phoneNumber, name, updatePhoneNumber, updateName]);

  return (
    <div
      className={`flex flex-col -mt-8 items-center justify-center min-h-screen px-6 transition-all duration-500 ${
        darkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      {/* Heading */}
      <div className="text-center mb-10 mt-10">
        <div className="flex items-center justify-center gap-4 mb-2">
          <FaUniversity className="text-5xl text-indigo-500 dark:text-indigo-400" />
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 bg-clip-text text-transparent">
            Welcome to PrintKaro.shop
          </h1>
        </div>
        <div className="flex items-center justify-center">
          <span className="text-gray-500 dark:text-gray-400 text-lg md:text-xl font-medium italic border-t-2 border-indigo-200 dark:border-indigo-800 pt-2">
            By JBSM Enterprises
          </span>
        </div>
      </div>

      {/* Main Content */}
      {phoneNumber && name ? (
        isUploadEnabled ? (
          <>
            <CollegeSelector />
            {selectedCollege && (
              <div className="mt-6 w-full max-w-lg">
                <FileUpload />
              </div>
            )}
          </>
        ) : (
          <div
            className={`w-full max-w-md p-6 rounded-lg shadow-lg ${
              darkMode ? "bg-gray-800" : "bg-white"
            } border ${darkMode ? "border-gray-700" : "border-gray-200"} text-center`}
          >
            <div className="flex flex-col items-center">
              <FaExclamationTriangle className="text-4xl text-yellow-500 mb-4" />
              <h2 className="text-2xl font-bold mb-2">Uploads Currently Disabled</h2>
              <p className="mb-4">
                We're not accepting new uploads at this time. Please check back later.
              </p>
              <div className={`text-sm p-3 rounded ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                For urgent requests, contact us at:
                <br />
                <span className="font-semibold">printkaro369@gmail.com</span>
              </div>
            </div>
          </div>
        )
      ) : null}

      {/* Phone Number Modal */}
      <AnimatePresence>
        {showPhoneModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className={`p-6 rounded-xl shadow-2xl max-w-md w-full mx-4 ${
                darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
              }`}
            >
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-center">
                Additional Information
              </h2>
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 mb-6 text-center">
                Please provide the following information to continue.
              </p>

              {/* Name Input */}
              {!name && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Your Name
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Enter your name"
                      className={`w-full p-3 pl-10 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        darkMode
                          ? "bg-gray-700 border-gray-600 text-white focus:ring-indigo-500"
                          : "bg-white border-gray-300 text-gray-900 focus:ring-indigo-500"
                      }`}
                    />
                    <FiUser
                      className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                        darkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    />
                  </div>
                </div>
              )}

              {/* Phone Number Input */}
              {!phoneNumber && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Your Phone Number
                  </label>
                  <div className="relative">
                    <div className="flex items-center">
                      <span
                        className={`p-3 border rounded-l-lg ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-gray-100 border-gray-300 text-gray-900"
                        }`}
                      >
                        +91
                      </span>
                      <input
                        type="text"
                        value={newPhoneNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                          setNewPhoneNumber(value);
                        }}
                        placeholder="Enter your phone number"
                        className={`w-full p-3 border rounded-r-lg focus:outline-none focus:ring-2 transition-all ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-white focus:ring-indigo-500"
                            : "bg-white border-gray-300 text-gray-900 focus:ring-indigo-500"
                        }`}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handlePhoneNumberSubmit}
                className={`w-full py-3 rounded-lg font-semibold transition-all transform hover:scale-105 active:scale-95 ${
                  darkMode
                    ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                    : "bg-indigo-500 hover:bg-indigo-600 text-white"
                }`}
              >
                Submit
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomePage;
